import React from "react";

// Comfortably inside the three minutes browserless waits for the dashboard, and the same
// ceiling ag-charts applies to waitForUpdate itself.
export const CHART_PAINT_FAILSAFE_MS = 10000;

const nextFrame = () => new Promise(resolve => requestAnimationFrame(() => resolve()));

// A scheduled report prints the moment every panel has reported loaded, so a panel that
// reports when its data arrives prints before its chart exists. ag-charts creates a chart
// synchronously but paints it over later frames; waitForUpdate settles once it has.
// Reports exactly once, and never waits forever: a report that never hears back fails
// outright, which is worse than one empty chart.
const useReportWhenPainted = ({expectsChart, chart, onReady}) => {
  const reportedRef = React.useRef(false);
  const onReadyRef = React.useRef(onReady);
  onReadyRef.current = onReady;

  const report = React.useCallback(() => {
    if (reportedRef.current) {
      return;
    }
    reportedRef.current = true;
    if (onReadyRef.current) {
      onReadyRef.current();
    }
  }, []);

  React.useEffect(() => {
    if (!expectsChart) {
      report();
      return undefined;
    }
    const failsafe = setTimeout(report, CHART_PAINT_FAILSAFE_MS);
    return () => clearTimeout(failsafe);
  }, [expectsChart, report]);

  React.useEffect(() => {
    if (expectsChart && chart) {
      // A new chart lays out at ag-charts' default 600x300 until a ResizeObserver hands it
      // the panel's size, and waitForUpdate only waits on updates already queued. Observers
      // run after animation frame callbacks, so two frames guarantee that resize is queued.
      nextFrame()
          .then(nextFrame)
          .then(() => chart.waitForUpdate())
          .then(report, report);
    }
  }, [expectsChart, chart, report]);
};

export default useReportWhenPainted;
